// The order row of a fleet card (ui-map-interface.md §8 "The order row",
// the owner's ruling of 2026-10-09 on mockup round 084 card 04).
//
// The row is [ACTION] HUNT, then ⋯ with every other order: one row of
// three presses, and HOLD stands in ⋯ (the owner's ruling of 2026-10-13,
// R2061). The
// action press is the order of the hull's class; an action that is not
// open yet falls back along a chain; a hull with no gun shows RETURN in
// the HUNT slot. The table is `[presentation.orders]`, which reaches this
// module as the generated `ORDERS`; the module holds no copy of it.
//
// Everything here is pure. `orderRow` places the orders; `fleetOrderGate`
// answers which orders a hull has now, from the client gates the order
// buttons always had (the catalog `dps` and tubes, the anchor, A8, A6, the
// Communications level of SHARE, the dredger brain). The phone peek of the
// context sheet calls `orderRow` too, with ▴ in place of ⋯.

import { Building, ShipClass } from '../../gen/proto/aquaphobia';
import { ORDERS, type OrderId } from '../../gen/presentation';
import type { FeatureFlag } from '../../gen/featureflags';
import { canAnchor } from '../format';

export type { OrderId };

/** Where each order of one hull stands on its card. */
export interface OrderRow {
  /** The action press: the card primary, in the dark face. */
  action: OrderId;
  /** The quiet order after it (one, R2061; the table may name two). */
  quiet: OrderId[];
  /** Every other open order, in the ⋯ order. */
  more: OrderId[];
}

/** The key of a hull class in `ORDERS.action`: the ship id in lower case. */
export function classKey(cls: ShipClass): string {
  const name = ShipClass[cls] ?? '';
  return name.replace(/^SHIP_CLASS_/, '').toLowerCase();
}

/**
 * The row of one hull.
 *
 * `open` answers whether the hull has an order now (its gates); `dps` is
 * the catalog gun of the class. An action that is not open takes the
 * first open order of the fallback chain, and the chain ends in an order
 * every hull has, so the row always has an action.
 */
export function orderRow(cls: ShipClass, dps: number, open: (o: OrderId) => boolean): OrderRow {
  const want = ORDERS.action[classKey(cls)];
  const action: OrderId = (want !== undefined && open(want) ? want : ORDERS.fallback.find(open))
    ?? ORDERS.fallback[ORDERS.fallback.length - 1];
  const quiet: OrderId[] = [];
  for (const q of ORDERS.quiet) {
    // The HUNT slot takes RETURN on a hull with no gun (dps 0).
    const o: OrderId = q === 'hunt' && !(dps > 0) ? ORDERS.noGun : q;
    if (o !== action && open(o) && !quiet.includes(o)) quiet.push(o);
  }
  const more = ORDERS.more.filter((o) => o !== action && !quiet.includes(o) && open(o));
  return { action, quiet, more };
}

/** What `fleetOrderGate` reads of the client state. */
export interface GateState {
  hasTechCode(code: string): boolean;
  /** The latched flags of the world (R1574): `routes` opens PLOT and
   *  closes PATROL (v2-the-deep.md §7, R2589). */
  hasFeatureFlag(flag: FeatureFlag): boolean;
  level(b: Building): number;
  rules: { oracleShareComms: number; workFitClasses?: readonly ShipClass[] };
  empire?: { dredgerBrain: boolean } | null;
}

/**
 * Does a hull class take a SEARCH (v2-the-deep.md §9.6, R2834)?
 * The server names the fit classes in `CatalogRules.work_fit_classes`:
 * every combat ship and the Dredger. The client mirrors no part of the
 * rule, and a store with no list takes no class.
 */
export function workFit(s: { rules: { workFitClasses?: readonly ShipClass[] } }, cls: ShipClass): boolean {
  return s.rules.workFitClasses?.includes(cls) ?? false;
}

/** What `fleetOrderGate` reads of the hull's catalog row. */
export interface GateSpec {
  dps: number;
  torpedoes: readonly unknown[];
  anchors: boolean;
}

/**
 * Which orders one own hull has now. These are the client gates of the
 * order buttons, unchanged (R79, R453, R661, R721, R722, R1226, R1437):
 * the server re-checks every order, and a gate only decides whether the
 * press stands.
 */
export function fleetOrderGate(s: GateState, cls: ShipClass, spec: GateSpec | undefined):
  (o: OrderId) => boolean {
  const gun = (spec?.dps ?? 0) > 0;
  // Under `routes` a loop course is the patrol of v2 (§7, §15.1): PLOT
  // replaces PATROL in ⋯ and in the fallback chain.
  const routes = s.hasFeatureFlag('routes');
  return (o) => {
    switch (o) {
      case 'fire': return (spec?.torpedoes.length ?? 0) > 0;
      case 'scan': return cls === ShipClass.SHIP_CLASS_VEDETTE;
      case 'gather': return cls === ShipClass.SHIP_CLASS_DREDGER;
      case 'share': return cls === ShipClass.SHIP_CLASS_ORACLE
        && s.level(Building.BUILDING_COMMUNICATIONS) >= s.rules.oracleShareComms;
      case 'anchor': return canAnchor(spec);
      case 'deep_run': return s.hasTechCode('A8');
      case 'hunt': case 'hold_fire': return gun;
      case 'decoy': return cls === ShipClass.SHIP_CLASS_VEDETTE && s.hasTechCode('A6');
      case 'auto_field': return cls === ShipClass.SHIP_CLASS_DREDGER && (s.empire?.dredgerBrain ?? false);
      // LAY and COLLECT of the Sapper (v2 §11.1, R2651): the class alone
      // has them, and the control reads the flag (§14.1 rule 5).
      case 'lay': case 'collect': return cls === ShipClass.SHIP_CLASS_SAPPER && s.hasFeatureFlag('sapper');
      // SEARCH of a fit hull (v2 §9.6, R2834), under the flag.
      case 'search': return s.hasFeatureFlag('treasure') && workFit(s, cls);
      case 'plot': return routes;
      case 'patrol': return !routes;
      case 'move': case 'escort': case 'defend': case 'return': return true;
    }
  };
}
